import { inject, Injectable } from "@angular/core";
import { AuthTokenResponse } from "@pcp/core/interfaces";
import { AuthClaims, AuthClaimsUpperCamelCase, ClaimsFeatureFlags, ClaimsFeatureFlagsUpperCamelCase } from "@pcp/core/interfaces";
import { TokenIsExpiredError, TokenIsInvalidError, TokensAndDecodedToken } from "../interfaces/token-error";
import { JwtHelperService } from "@auth0/angular-jwt";
import { TimeSkewService } from "./time-skew.service";
import { DateTime } from 'luxon';
import { PCP_LOGGER, PcpLoggerInterface } from "@pcp/logging";

@Injectable({
    providedIn: 'root'
})
export class PcpTokenDecoderService {

    private readonly jwtHelper = inject(JwtHelperService);
    private readonly timeSkewService = inject(TimeSkewService);
    private readonly log = inject(PCP_LOGGER).createScoped('auth');

    public decodeAndValidateToken(
        tokens: Pick<AuthTokenResponse, 'token' | 'refreshToken'>,
        isFreshFromServer = false
    ): TokensAndDecodedToken {
        const { token, refreshToken } = tokens;

        // 1. FIRST: Decode token claims (before validation)
        const claims = this.decodeToken(token);
        const expiresAtDate = this.jwtHelper.getTokenExpirationDate(token);
        const expiresAt = expiresAtDate ? DateTime.fromJSDate(expiresAtDate) : null;
        const issuedAt = claims.iat;

        // 2. SECOND: Detect skew from fresh tokens only.
        // Only detect skew when we know the token was just received from server.
        if (isFreshFromServer) {
            this.timeSkewService.detectSkewFromFreshToken(claims);
        }

        // 3. THIRD: Apply detected clock skew to token validation
        const offsetSeconds = this.timeSkewService.getOffsetSeconds();
        const isExpired = this.jwtHelper.isTokenExpired(token, offsetSeconds);

        // iat is in the future => not possible
        // Client clock must be wrong.
        const iatIsInTheFuture = issuedAt > DateTime.now() && !isFreshFromServer;
        if (!isFreshFromServer && iatIsInTheFuture) {
            this.log.error('Token iat is in the future');
            this.log.trackEvent('TokenIatInFuture', {
                userId: claims.userId,
                customer: claims.customer,
                iat: issuedAt.toISO(),
                now: DateTime.now().toISO(),
            });
        }

        if (token && refreshToken && expiresAt instanceof DateTime && claims) {
            if (isExpired || iatIsInTheFuture) {
                throw new TokenIsExpiredError(token, refreshToken, claims, expiresAt);
            } else {
                return { token, refreshToken, claims, expiresAt, issuedAt };
            }
        }

        throw new TokenIsInvalidError(token, refreshToken);
    }

    private decodeToken(token: string): AuthClaims {
        // claims as returned by the API in UpperCamelCase
        // e.g. `Customer` instead of `customer`
        const claimsUpperCamelCase = this.jwtHelper.decodeToken<AuthClaimsUpperCamelCase>(token);

        if (!claimsUpperCamelCase) {
            throw new Error('Failed to decode claims');
        }

        return mapClaimsToState(claimsUpperCamelCase, this.log);
    }

}

const mapClaimsToState = (claims: AuthClaimsUpperCamelCase, log: PcpLoggerInterface): AuthClaims => ({
    hasDriverscam: !!claims.HasDriverscam && claims.HasDriverscam.toLowerCase() === 'true',
    sas: claims.Sas,
    customer: claims.Customer,
    customerName: claims.CustomerName,
    userId: claims.UserId,
    userName: claims.UserName,
    serials: claims.Serials ? JSON.parse(claims.Serials) : undefined,
    enetToken: claims.EnetToken,
    hasPushTheButton: !!claims.HasPushTheButton && claims.HasPushTheButton.toLowerCase() === 'true',
    hasPracticeTrainer: !!claims.HasPracticeTrainer && claims.HasPracticeTrainer.toLowerCase() === 'true',
    clientType: claims.ClientType,
    lessonId: claims.LessonId ?? undefined,
    featureFlags: parseFeatureFlags(claims, log),
    iat: DateTime.fromSeconds(claims.iat),
    nbf: DateTime.fromSeconds(claims.nbf),
});

const parseFeatureFlags = (claims: AuthClaimsUpperCamelCase, log: PcpLoggerInterface): ClaimsFeatureFlags => {
    let claimsFeatureFlags: ClaimsFeatureFlagsUpperCamelCase;
    try {
        claimsFeatureFlags = JSON.parse(claims.FeatureFlags) as ClaimsFeatureFlagsUpperCamelCase;
    } catch (error) {
        log.warn('Error while parsing feature flags', { error: String(error) });
        claimsFeatureFlags = {
            PracticeTrainerEnabled: false,
        }
    }

    return {
        practiceTrainerEnabled: claimsFeatureFlags.PracticeTrainerEnabled ?? false,
    };
};
